import React, { useContext, useEffect, useState, useRef } from 'react';
import axios from "axios";
import { Button, Card, CardGroup, Col, Collapse, Form, FormControl, InputGroup, ListGroup, OverlayTrigger, Row, Spinner, Tooltip } from "react-bootstrap";
import { Link, navigate, useQueryParams } from "raviger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircle, faExclamationCircle, faInfoCircle, faQuestionCircle, faUserAlt, faUserAltSlash, faUsers, faFilePdf,
  faFileCsv
} from '@fortawesome/free-solid-svg-icons';
import {
  faDotCircle
} from '@fortawesome/free-regular-svg-icons';
import { faHomeAlt, faChevronDoubleDown, faChevronDoubleUp, faLocationCrosshairs } from '@fortawesome/pro-solid-svg-icons';
import Select from 'react-select';
import { useMark, useSubmitting } from '../hooks';
import Moment from "react-moment";
import moment from 'moment';
import Header from '../components/Header';
import Scrollbar from '../components/Scrollbars';
import Paginator from '../components/Paginator';
import { ITEMS_PER_PAGE } from '../constants';
import { DateRangePicker } from '../components/Form';
import { SystemErrorContext } from '../components/SystemError';
import { countDictMatches, prettyText } from "../components/Map";
import { printAllDispatchResolutions } from './Utils';
import LoadingLink from "../components/LoadingLink";
import ActionsDropdown from '../components/ActionsDropdown';
import { capitalize } from '../utils/formatString';
import { AuthContext } from "../accounts/AccountsReducer";

function DispatchAssignmentSearch({ incident, organization }) {

  const { setShowSystemError } = useContext(SystemErrorContext);
  const { state } = useContext(AuthContext);

  // Identify any query param data.
  const [queryParams] = useQueryParams();
  const {
    search = '',
    status = '',
  } = queryParams;

  const tempSearchTerm = useRef(null);
  const statusRef = useRef(null);
  const openStartDateRef = useRef(null);
  const openEndDateRef = useRef(null);

  const [data, setData] = useState({evacuation_assignments: [], total_count: 0, isFetching: true});
  const [searchTerm, setSearchTerm] = useState(search);
  const [showFilters, setShowFilters] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [options, setOptions] = useState({id:null, status:status, open_start:null, open_end:null, dispatch_start:null, dispatch_end:null});
  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [matches, setMatches] = useState({});
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [goToID, setGoToID] = useState('');
  const { markInstances } = useMark();
  const [species, setSpecies] = useState([]);
  const {
    handleSubmitting,
    submittingComplete
  } = useSubmitting();

  // Update searchTerm when field input changes.
  const handleChange = event => {
    tempSearchTerm.current.value = event.target.value;
  };

  const handleIDChange = async event => {
    setGoToID(event.target.value);
  };

  // Use searchTerm to filter service_requests.
  const handleSubmit = async event => {
    event.preventDefault();
    setSearchTerm(tempSearchTerm.current.value);
    setPage(1);
  }

  const handleClear = () => {
    statusRef.current.clearValue();
    if (openStartDateRef.current && openStartDateRef.current.flatpickr) {
      openStartDateRef.current.flatpickr.clear();
    }
    if (openEndDateRef.current && openEndDateRef.current.flatpickr) {
      openEndDateRef.current.flatpickr.clear();
    }
    setOptions({id:null, status:status, open_start:null, open_end:null, dispatch_start:null, dispatch_end:null});
    setTriggerRefresh(!triggerRefresh);
  };

  const handlePrintAllClick = async (e) => {
    setIsLoading(true);

    let dispatch_assignments = [];
    let nextUrl = '/evac/api/evacassignment/?page=' + page + '&map=true&search=' + searchTerm + '&incident=' + incident + '&organization=' + organization;
    do {
      const response = await axios.get(nextUrl, {
        params: {
          status: options.status,
          open_start: options.open_start,
          open_end: options.open_end,
          dispatch_start: options.dispatch_start,
          dispatch_end: options.dispatch_end,
        },
      })
      .catch(error => {
        if (error.response) {
          setShowSystemError(true);
        }
      });

      dispatch_assignments.push(...response.data.results);
      nextUrl = response.data.next;
      if (nextUrl) {
        nextUrl = '/evac/' + response.data.next.split('/evac/')[1];
      }
    } while(nextUrl != null)

    handleSubmitting()
      .then(() => printAllDispatchResolutions({
        drs: dispatch_assignments,
        organization: state.organization.name,
        incident: state.incident.name
      }))
      .then(submittingComplete)
      .finally(() => setIsLoading(false));
  }

  const downloadCsv = () => {
    const headers = [
      'ID',
      'Status',
      'Opened Date',
      'Closed Date',
      'Team Name',
      'Team Members',
      'Service Request IDs'
    ];

    const rows = data.evacuation_assignments.map(item => {
      return [
        "FA#" + String(item.id_for_incident),
        item.end_time ? 'Closed' : item.end_time === null && item.team_member_names ? 'Active' : 'Preplanned',
        item.start_time,
        item.end_time,
        item.team_object?.name||"N/A",
        item.team_member_names||"N/A",
        item.assigned_requests.map(assigned_request => "SR#" + String(assigned_request.service_request_object.id_for_incident)).join(', ')||"N/A",
      ];
    });

    const csv = [
      headers.join(','),
      ...rows.map(row =>
        row.map(value =>
          `"${String(value ?? '').replace(/"/g, '""')}"`
        ).join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${organization}-${incident}-field-assignment-list.csv`;
    link.click();

    URL.revokeObjectURL(link.href);
  };

  const customStyles = {
    // For the select it self, not the options of the select
    control: (styles, { isDisabled}) => {
      return {
        ...styles,
        color: '#FFF',
        cursor: isDisabled ? 'not-allowed' : 'default',
        backgroundColor: isDisabled ? '#DFDDDD' : 'white',
        height: 35,
        minHeight: 35,
        marginBottom: "15px"
      }
    },
    option: provided => ({
      ...provided,
      color: 'black'
    }),
  };

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();
    tempSearchTerm.current.focus();

    const fetchSpecies = () => {
      // Fetch Species data.
      axios.get('/animals/api/species/', {
        cancelToken: source.token,
      })
      .then(speciesResponse => {
        if (!unmounted) {
          let species_options = [];
          speciesResponse.data.forEach(result => {
            // Build species option list.
            species_options.push({value: result.name, label: capitalize(result.name)});
          });
          setSpecies(speciesResponse.data);
          fetchDispatchAssignments(speciesResponse.data);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    };
    fetchSpecies();

    const fetchDispatchAssignments = async (species_data) => {
      // Fetch DispatchAssignment data.
      await axios.get('/evac/api/evacassignment/?page=' + page + '&map=true&search=' + searchTerm + '&incident=' + incident + '&organization=' + organization, {
        cancelToken: source.token,
        params: {
          status: options.status,
          open_start: options.open_start,
          open_end: options.open_end,
          dispatch_start: options.dispatch_start,
          dispatch_end: options.dispatch_end,
        },
      })
      .then(response => {
        if (!unmounted) {
          setNumPages(Math.ceil(response.data.count / ITEMS_PER_PAGE));
          setData({evacuation_assignments: response.data.results, total_count: response.data.count, isFetching: false});
          let map_dict = {};
          for (const dispatch_assignment of response.data.results) {
            for (const assigned_request of dispatch_assignment.assigned_requests) {
              const [species_matches, status_matches] = countDictMatches(assigned_request.animals, species_data, true);
              map_dict[assigned_request.service_request_object.id] = {species_matches:species_matches, status_matches:status_matches};
            }
          }
          setMatches(map_dict);

          // highlight search terms
          markInstances(searchTerm);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setData({evacuation_assignments: [], total_count: 0, isFetching: false});
          setShowSystemError(true);
        }
      });
    };

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [incident, searchTerm, triggerRefresh, page]);

  // Hook handling option changes.
  useEffect(() => {
    setIsDisabled(!(options.id || options.status || options.open_start || options.open_end || options.dispatch_start || options.dispatch_end));
  }, [options]);

  return (
    <div className="ms-2 me-2">
      <Header>Search Field Assignments</Header>
      <Form onSubmit={handleSubmit}>
        <Row>
          <Col xs="2" style={{maxWidth:"150px", marginRight:"-10px", paddingRight:"0px"}}>
            <InputGroup>
              <FormControl
                type="text"
                placeholder="ID #"
                name="searchIDTerm"
                onChange={handleIDChange}
              />
              <Button variant="outline-light" type="submit" disabled={!goToID} style={{borderRadius:"0 5px 5px 0", color:"white"}} onClick={(e) => {navigate("/" + organization + "/" + incident + "/field/summary/" + goToID)}}>Go</Button>
            </InputGroup>
          </Col>
          <Col>
            <InputGroup>
              <FormControl
                  type="text"
                  placeholder="Search"
                  name="searchTerm"
                  onChange={handleChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSubmit(e);
                    }
                  }}
                  ref={tempSearchTerm}
              />
              <Button variant="outline-light" type="submit" style={{borderRadius:"0 5px 5px 0", color:"white"}}>Search
                <OverlayTrigger
                  key={"search-information"}
                  placement="right"
                  overlay={
                    <Tooltip id={`tooltip-search-information`}>
                      Searchable fields: team name, team member names, animal names, and service request address fields.
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="ms-1 fa-move-up" size="sm" inverse />
                </OverlayTrigger>
              </Button>
              <Button variant="outline-light" className="ms-1 me-1 rounded" style={{height:"38px", color:"white"}} onClick={() => {setShowFilters(!showFilters)}}>Advanced {showFilters ? <FontAwesomeIcon icon={faChevronDoubleUp} className="fa-move-up" size="sm" /> : <FontAwesomeIcon icon={faChevronDoubleDown} className="fa-move-up" size="sm" />}</Button>
              {isLoading ? (
              <div className="d-flex" style={{width:"148px", justifyContent:"center"}}>
                <Spinner
                  {...{
                    as: 'span',
                    animation: 'border',
                    size: undefined,
                    role: 'status',
                    'aria-hidden': 'true',
                    variant: 'light',
                    style: {
                      height: '2rem',
                      width: '2rem',
                      marginBottom: '0.25rem'
                    }
                  }}
                />
              </div>
            ) : (
              <ActionsDropdown variant="outline-light" title={"Download All" + " (" + `${data.total_count}` + ")"} search={true} disabled={data.isFetching || data.evacuation_assignments.length === 0}>
                <LoadingLink label="Field Assignment Summaries" onClick={handlePrintAllClick} isLoading={data.isFetching} className="text-white d-block">
                  <FontAwesomeIcon icon={faFilePdf} inverse />
                </LoadingLink>
                <LoadingLink onClick={() => downloadCsv()} label="Field Assignment List" disabled={data.isFetching} className="d-block">
                  <FontAwesomeIcon icon={faFileCsv} className="fas" style={{cursor:'pointer'}} inverse />
                </LoadingLink>
              </ActionsDropdown>
            )}
            </InputGroup>
          </Col>
        </Row>
        <Collapse in={showFilters}>
          <div>
          <Card className="border rounded d-flex mt-3" style={{width:"100%"}}>
            <Card.Body>
              <Row className="gx-2" style={{marginBottom:"-5px"}}>
                <Col xs={"4"} style={{textTransform:"capitalize"}}>
                  <Select
                    label="Status"
                    id="statusDropdown"
                    name="Status"
                    type="text"
                    placeholder="Select Status"
                    options={[{ value: 'preplanned', label: 'Preplanned' },{ value: 'active', label: 'Active' },{ value: 'resolved', label: 'Closed' },]}
                    styles={customStyles}
                    isClearable={true}
                    ref={statusRef}
                    onChange={(instance) => {
                      setOptions({...options, status: instance ? instance.value : null});
                    }}
                  />
                </Col>
                <Col xs={4} />
                <Col className="flex-grow-1 float-end" xs={3}>
                  <Button className="btn btn-primary" style={{maxHeight:"36px", width:"100%"}} onClick={() => {tempSearchTerm.current.value !== searchTerm ? setSearchTerm(tempSearchTerm.current.value) : setTriggerRefresh(!triggerRefresh);}} disabled={isDisabled}>Apply</Button>
                </Col>
              </Row>
              <Row className="gx-2" style={{marginBottom:"0px", maxHeight:"37px"}}>
                <Col xs={4}>
                  <DateRangePicker
                    name={`open_start`}
                    id={`open_start`}
                    mode="single"
                    placeholder={"Filter by Opened Start Date"}
                    style={{height:"36px"}}
                    data-enable-time={false}
                    ref={openStartDateRef}
                    clearable={"true"}
                    value={options.open_start || null}
                    onChange={(dateRange, instance) => {
                      setOptions({...options, open_start: dateRange.length ? dateRange[0] : null});
                    }}
                  />
                </Col>
                <Col xs={4}>
                  <DateRangePicker
                    name={`open_end`}
                    id={`open_end`}
                    mode="single"
                    placeholder={"Filter by Opened End Date"}
                    style={{height:"36px"}}
                    data-enable-time={false}
                    ref={openEndDateRef}
                    clearable={"true"}
                    value={options.open_end || null}
                    onChange={(dateRange) => {
                      setOptions({...options, open_end:dateRange.length ? dateRange[0] : null});
                    }}
                  />
                </Col>
                <Col className="flex-grow-1" xs="3">
                  <Button className="mb-3" variant="outline-light" style={{maxHeight:"36px", width:"100%"}} onClick={handleClear}>Clear</Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          </div>
        </Collapse>
      </Form>
      {data.evacuation_assignments.map((evacuation_assignment, index) => (
        <div key={evacuation_assignment.id} className="mt-3 border rounded" hidden={page !== Math.ceil((index+1)/ITEMS_PER_PAGE)}>
          <div className="card-header d-flex hide-scrollbars" style={{whiteSpace:'nowrap', overflow:"hidden"}}>
            <h4 style={{marginBottom:"-2px", marginLeft:"-12px", whiteSpace:'nowrap', overflow:"hidden", textOverflow:"ellipsis"}}>
              <OverlayTrigger
                key={"dispatch-assignment-summary"}
                placement="top"
                overlay={
                  <Tooltip id={`tooltip-dispatch-assignment-summary`}>
                    Field assignment summary
                  </Tooltip>
                }
              >
                <Link href={"/" + organization + "/" + incident + "/field/summary/" + evacuation_assignment.id_for_incident}><FontAwesomeIcon icon={faDotCircle} className="me-2" inverse /></Link>
              </OverlayTrigger>
              FA#{evacuation_assignment.id_for_incident}
              {evacuation_assignment.team && evacuation_assignment.team_name ?
                <span> -&nbsp;{evacuation_assignment.team_name}
                  <OverlayTrigger
                    key={"team-names"}
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-team-names`}>
                        {evacuation_assignment.team_member_names}
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faUsers} className="ms-1" />
                  </OverlayTrigger>
                </span> : evacuation_assignment.team_member_names ?
                <span title={evacuation_assignment.team_member_names}> - {evacuation_assignment.team_member_names}</span> : ""}
            </h4>
          </div>
          <CardGroup style={{overflowX:"hidden", marginBottom:"-6px"}}>
            <Card style={{marginBottom:"6px", maxWidth:"300px"}}>
              <Card.Body>
                <Card.Title style={{marginTop:"-9px", marginBottom:"8px"}}>Information</Card.Title>
                <Scrollbar style={{height:"144px"}} renderThumbHorizontal={props => <div {...props} style={{...props.style, display: 'none'}} />}>
                  <ListGroup>
                    <ListGroup.Item><b>Status: </b>{evacuation_assignment.end_time ? 'Closed' : evacuation_assignment.end_time === null && evacuation_assignment.team_member_names ? 'Active' : 'Preplanned'}</ListGroup.Item>
                    <ListGroup.Item><b>Opened: </b><Moment format="L">{evacuation_assignment.start_time}</Moment></ListGroup.Item>
                    {evacuation_assignment.end_time ?
                      <ListGroup.Item><b>Closed: </b><Moment format="L">{evacuation_assignment.end_time}</Moment></ListGroup.Item>
                    : ""}
                  </ListGroup>
                </Scrollbar>
              </Card.Body>
            </Card>
            <Card style={{maxHeight:"208px"}}>
              <Scrollbar style={{height:"206px"}} renderView={props => <div {...props} style={{...props.style, overflow:"auto"}}/>} renderThumbHorizontal={props => <div {...props} style={{...props.style, display:"none"}} />}>
                <Card.Body style={{marginBottom:"0px", overflow:"hidden"}}>
                  <Card.Title style={{marginTop:"-10px", marginBottom:"7px", marginLeft:"-9px"}}>Service Requests</Card.Title>
                  {evacuation_assignment.assigned_requests.map(assigned_request => (
                    <div key={assigned_request.service_request_object.id} style={{marginLeft:"-10px", marginRight:"7px", marginTop: "7px", marginBottom:"0px"}}>
                      <div className="card-header border rounded">
                      <span style={{marginLeft:"-12px"}}>
                      {matches[assigned_request.service_request_object.id] && Object.keys(matches[assigned_request.service_request_object.id].status_matches['REPORTED']).length > 0 ?
                        <OverlayTrigger
                          key={"reported"}
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-utl`}>
                              {Object.keys(matches[assigned_request.service_request_object.id].status_matches['REPORTED']).map((key,i) => (
                                <span key={key} style={{textTransform:"capitalize"}}>
                                  {i > 0 && ", "}{prettyText('', key.split(',')[0], matches[assigned_request.service_request_object.id].status_matches['REPORTED'][key], species)}
                                </span>
                              ))}
                              {assigned_request.service_request_object.reported_animals > 1 ? " are":" is"} reported
                            </Tooltip>
                          }
                        >
                          <FontAwesomeIcon icon={faExclamationCircle} className="me-1"/>
                        </OverlayTrigger>
                        : ""}
                        {matches[assigned_request.service_request_object.id] && Object.keys(matches[assigned_request.service_request_object.id].status_matches['SHELTERED IN PLACE']).length > 0 ?
                        <OverlayTrigger
                          key={"sip"}
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-sip`}>
                              {Object.keys(matches[assigned_request.service_request_object.id].status_matches['SHELTERED IN PLACE']).map((key,i) => (
                                <span key={key} style={{textTransform:"capitalize"}}>
                                  {i > 0 && ", "}{prettyText('', key.split(',')[0], matches[assigned_request.service_request_object.id].status_matches['SHELTERED IN PLACE'][key], species)}
                                </span>
                              ))}
                              {assigned_request.service_request_object.sheltered_in_place > 1 ? " are":" is"} sheltered in place
                            </Tooltip>
                          }
                        >
                          <span className="fa-layers me-1">
                            <FontAwesomeIcon icon={faCircle} transform={'grow-1'} />
                            <FontAwesomeIcon icon={faHomeAlt} style={{color:"#444"}} transform={'shrink-3'} size="sm" inverse />
                          </span>
                        </OverlayTrigger>
                        : ""}
                        {matches[assigned_request.service_request_object.id] && Object.keys(matches[assigned_request.service_request_object.id].status_matches['UNABLE TO LOCATE']).length > 0 ?
                        <OverlayTrigger
                          key={"utl"}
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-utl`}>
                              {Object.keys(matches[assigned_request.service_request_object.id].status_matches['UNABLE TO LOCATE']).map((key,i) => (
                                <span key={key} style={{textTransform:"capitalize"}}>
                                  {i > 0 && ", "}{prettyText('', key.split(',')[0], matches[assigned_request.service_request_object.id].status_matches['UNABLE TO LOCATE'][key], species)}
                                </span>
                              ))}
                              {assigned_request.service_request_object.unable_to_locate > 1 ? " are":" is"} unable to be located
                            </Tooltip>
                          }
                        >
                          <FontAwesomeIcon icon={faQuestionCircle} className="me-1"/>
                        </OverlayTrigger>
                        : ""}
                        {Object.keys(matches[assigned_request.service_request_object.id].species_matches).length === 0 ? <OverlayTrigger
                          key={"support"}
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-support`}>
                              Support request
                            </Tooltip>
                          }
                        >
                          <FontAwesomeIcon icon={faLocationCrosshairs} className="me-1"/>
                        </OverlayTrigger> : ""}
                        </span>
                        <span>SR#{assigned_request.service_request_object.id_for_incident} - <Link href={"/" + organization + "/" + incident + "/hotline/servicerequest/" + assigned_request.service_request_object.id_for_incident} className="text-link" style={{textDecoration:"none", color:"white"}}>{assigned_request.service_request_object.full_address}</Link> |
                        {assigned_request.service_request_object.owner_objects.length === 0 ?
                          <OverlayTrigger
                            key={"stray"}
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-stray`}>
                                No owner
                              </Tooltip>
                            }
                          >
                            <FontAwesomeIcon icon={faUserAltSlash} className="ms-1 me-1" size="sm" />
                          </OverlayTrigger> :
                          <OverlayTrigger
                            key={"stray"}
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-stray`}>
                                {assigned_request.service_request_object.owner_objects.map(owner => (
                                  <div key={owner.id}>{owner.first_name} {owner.last_name}</div>
                                ))}
                              </Tooltip>
                            }
                          >
                            <FontAwesomeIcon icon={faUserAlt} className="ms-1 me-1" size="sm" />
                          </OverlayTrigger>
                        }
                        </span>
                        {matches[assigned_request.service_request_object.id] ?
                        <span>
                          {Object.keys(matches[assigned_request.service_request_object.id].species_matches).map((key,i) => (
                            <span key={key} style={{textTransform:"capitalize"}}>
                              {i > 0 && ", "}{prettyText(key.split(',')[0], matches[assigned_request.service_request_object.id].species_matches[key], species)}
                            </span>
                          ))}
                        </span>
                        :""}
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Scrollbar>
            </Card>
          </CardGroup>
        </div>
      ))}
      <p className="mt-3">{data.isFetching ? 'Fetching field assignments...' : <span>{data.evacuation_assignments.length ? '' : 'No field assignments found.'}</span>}</p>
      <Row className="mb-3">
        <Col xs={12} className="ps-0">
          <Paginator page={page} numPages={numPages} setPage={setPage} />
        </Col>
      </Row>
    </div>
  )
}

export default DispatchAssignmentSearch;
